"use client";

import { RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";

import { TwitchDebugInfo } from "@/experimental/twitch/twitch-debug-info";
import { TwitchLimitationsInfo } from "@/experimental/twitch/twitch-limitations-info";
import { Button } from "@/shared/ui/buttons/button";
import { Card } from "@/shared/ui/cards/card";
import { Skeleton } from "@/shared/ui/data-display/skeleton";

import { useTwitchStreams, StreamData } from "../api/use-twitch";
import { TwitchStreamCard } from "../twitch/TwitchStreamCard";

import { VideoPlayer } from "./video-player";

interface StreamListProps {
  streams?: StreamData[] | null;
  title?: string;
  className?: string;
}

interface TwitchStreamPlayerProps {
  className?: string;
  gameId?: string;
  limit?: number;
}

export function StreamList({
  streams,
  title,
  className = "",
}: StreamListProps) {
  const tUi = useTranslations("Ui");
  if (!streams || streams.length === 0) {
    return (
      <div className={`space-y-6 ${className}`}>
        {title && <h2 className="text-2xl font-bold">{title}</h2>}
        <div className="text-sm text-muted-foreground">
          {tUi("StreamList.netDostupnyhStrimov")}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {title && <h2 className="text-2xl font-bold">{title}</h2>}
      <div className="space-y-4">
        {streams.map((stream: StreamData) => (
          <VideoPlayer key={stream.id} stream={stream} />
        ))}
      </div>
    </div>
  );
}

export function TwitchStreamList({
  className = "",
  gameId,
  limit = 6,
}: TwitchStreamPlayerProps) {
  const tUi = useTranslations("Ui");
  const { streams, loading, error, refetch } = useTwitchStreams(gameId, limit);
  const safeStreams = Array.isArray(streams) ? streams : [];

  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{tUi("StreamList.pryamyeTranslyatsii")}</h2>
          <Skeleton className="h-8 w-8" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-1 p-6 space-y-4">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                  <Skeleton className="h-8 w-full" />
                </div>
                <div className="lg:col-span-2">
                  <Skeleton className="aspect-video w-full" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{tUi("StreamList.pryamyeTranslyatsii")}</h2>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {tUi("StreamList.obnovit")}
          </Button>
        </div>
        <Card className="p-8 text-center">
          <div className="text-red-500 mb-2">{tUi("StreamList.oshibkaZagruzkiStrimov")}</div>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => void refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {tUi("StreamList.poprobovatSnova")}
          </Button>
        </Card>
      </div>
    );
  }

  if (safeStreams.length === 0) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{tUi("StreamList.pryamyeTranslyatsii")}</h2>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {tUi("StreamList.obnovit")}
          </Button>
        </div>
        <Card className="p-8 text-center">
          <div className="text-muted-foreground">{tUi("StreamList.netAktivnyhStrimov")}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{tUi("StreamList.pryamyeTranslyatsii")}</h2>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {tUi("StreamList.obnovit")}
        </Button>
      </div>

      <TwitchLimitationsInfo />
      <TwitchDebugInfo />

      <div className="space-y-4">
        {safeStreams.map((stream: StreamData) => (
          <TwitchStreamCard key={stream.id} stream={stream} />
        ))}
      </div>
    </div>
  );
}
